import { Component, computed, effect, inject, input, signal } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { of } from 'rxjs';
import { ButtonModule } from '@openng/optimus-ui/button';
import { DialogModule } from '@openng/optimus-ui/dialog';
import { MessageModule } from '@openng/optimus-ui/message';
import { rxResourceFixed, BreakpointService, LocalStorageManagerService } from '@zayan-erp/shared';
import { DsmApsService } from '../../../services/dsm-aps.service';
import { DsmDealOptionsService } from '../../../services/dsm-deal-options.service';
import { DsmApsDealContextService } from '../../../services/dsm-aps-deal-context.service';
import { DsmDealOption } from '../../../models/dsm-deal-options-view.models';
import {
  DsmRoomReplacementReviewExecution,
  DsmRoomReplacementReviewGroup,
  DsmRoomReplacementReviewRoom
} from '../../../models/dsm-room-replacement-review.models';
import { toActionableReplacements, toExecutedTriggerProductIds } from '../../../utils/dsm-room-replacement-review.util';
import { DsmApsAddendumOptionDetailsComponent } from '../dsm-aps-addendum-option-details/dsm-aps-addendum-option-details.component';
import { DsmApsAddendumOptionsComponent } from '../dsm-aps-addendum-options/dsm-aps-addendum-options.component';
import { DsmApsAddendumPreviewComponent } from '../dsm-aps-addendum-preview/dsm-aps-addendum-preview.component';

interface ResolvedMismatchGroup extends DsmRoomReplacementReviewGroup {
  addendum: DsmDealOption | null;
  mismatchedProductIds: ReadonlySet<string>;
}

@Component({
  selector: 'dsm-aps-review-list',
  standalone: true,
  host: { class: 'flex flex-col flex-1 overflow-hidden' },
  imports: [
    DsmApsAddendumOptionDetailsComponent,
    DsmApsAddendumOptionsComponent,
    DsmApsAddendumPreviewComponent,
    ButtonModule,
    DialogModule,
    MessageModule
  ],
  templateUrl: './dsm-aps-review-list.component.html',
  styleUrl: './dsm-aps-review-list.component.scss'
})
export class DsmApsReviewListComponent {
  dealId = input.required<string>();

  readonly #dsmApsService = inject(DsmApsService);
  readonly #dealOptionsService = inject(DsmDealOptionsService);
  readonly #breakpointService = inject(BreakpointService);
  readonly #dealContext = inject(DsmApsDealContextService);
  readonly #localStorageManager = inject(LocalStorageManagerService);

  isMobile = toSignal(this.#breakpointService.isMobile$, { initialValue: false });

  collapsedAddendumIds = signal<Set<string>>(new Set());

  selectedAddendumId = signal<string | null>(null);
  selectedProductId = signal<string | null>(null);
  selectedKitProductId: string | null = null;

  dealResource = rxResourceFixed({
    params: () => ({ dealId: this.dealId() }),
    defaultValue: null,
    stream: ({ params }) => {
      if (!params.dealId) return of(null);
      return this.#dsmApsService.getDealHeader(params.dealId);
    }
  });

  lotId = computed(() => this.dealResource.value()?.lot?.id ?? '');
  modelId = computed(() => this.dealResource.value()?.model?.id ?? '');
  elevationId = computed(() => this.dealResource.value()?.elevation?.id ?? '');

  reviewResource = rxResourceFixed({
    params: () => ({ dealId: this.dealId() }),
    defaultValue: null,
    stream: ({ params }) => {
      if (!params.dealId) return of(null);
      return this.#dsmApsService.getRoomReplacementReview(params.dealId);
    }
  });

  addendumsResource = rxResourceFixed({
    params: () => ({ lotId: this.lotId(), dealId: this.dealId() }),
    defaultValue: [] as DsmDealOption[],
    stream: ({ params }) => {
      if (!params.lotId || !params.dealId) return of([]);
      return this.#dealOptionsService.getAddendums(params.lotId, params.dealId, false);
    }
  });

  constructor() {
    // dsm-aps-addendum-preview reads the deal from the shared context service, not an input —
    // seed it here the same way the Addendums tab does, so it works regardless of tab visit order.
    effect(() => {
      const deal = this.dealResource.value();
      if (deal) {
        this.#dealContext.currentDeal.set(deal);
      }
    });

    effect(() => {
      if (this.#dealContext.reloadDeal()) {
        this.dealResource.reload();
        this.reviewResource.reload();
        this.addendumsResource.reload();
        this.#dealContext.reloadDeal.set(false);
      }
    });
  }

  review = computed(() => this.reviewResource.value());
  isLoading = computed(() => this.reviewResource.isLoading());

  replacements = computed<DsmRoomReplacementReviewExecution[]>(() => toActionableReplacements(this.review()));
  hasReplacements = computed(() => (this.review()?.replacements?.length ?? 0) > 0);
  executedTriggerProductIds = computed(() => toExecutedTriggerProductIds(this.review()));

  // Room Replacement Rules: same "executed rule" banner + acknowledge-once disclosure as the
  // Addendums tab, sharing the 'dsm' source so acknowledging in either place covers both.
  allExecutions = computed<DsmRoomReplacementReviewExecution[]>(() => this.review()?.replacements ?? []);

  executedRulesBannerDismissed = signal(false);

  acknowledgedExecutionIds = signal<Set<string>>(
    this.#localStorageManager.getAcknowledgedRoomReplacementExecutionIds('dsm')
  );

  pendingDisclosureExecutions = computed<DsmRoomReplacementReviewExecution[]>(() =>
    this.allExecutions().filter(execution => !this.acknowledgedExecutionIds().has(execution.executionId))
  );

  currentDisclosure = computed<DsmRoomReplacementReviewExecution | null>(
    () => this.pendingDisclosureExecutions()[0] ?? null
  );

  currentDisclosureLabel = computed<string | null>(() => {
    const execution = this.currentDisclosure();
    return execution ? this.executionLabel(execution) : null;
  });

  acknowledgeCurrentDisclosure(): void {
    const execution = this.currentDisclosure();
    if (!execution) return;
    this.#localStorageManager.acknowledgeRoomReplacementExecution(execution.executionId, 'dsm');
    this.acknowledgedExecutionIds.update(ids => new Set(ids).add(execution.executionId));
  }

  private groupedLines = computed<DsmRoomReplacementReviewGroup[]>(() => {
    const lines = this.review()?.mismatchedLines ?? [];
    const groups = new Map<string, DsmRoomReplacementReviewGroup>();

    for (const line of lines) {
      const existing = groups.get(line.addendumId);
      if (existing) {
        existing.lines.push(line);
        continue;
      }
      groups.set(line.addendumId, {
        addendumId: line.addendumId,
        addendumSequenceNumber: line.addendumSequenceNumber,
        addendumType: line.addendumType,
        isAddendumLocked: line.isAddendumLocked,
        lines: [line]
      });
    }

    return Array.from(groups.values()).sort(
      (a, b) => (a.addendumSequenceNumber ?? 0) - (b.addendumSequenceNumber ?? 0)
    );
  });

  /** Each addendum that holds a mismatched selection, with its full DsmDealOption attached — the
   *  card renders with dsm-aps-addendum-preview / dsm-aps-addendum-options, the exact components
   *  the Addendums tab uses, showing every option on the addendum with the mismatched one flagged. */
  resolvedGroups = computed<ResolvedMismatchGroup[]>(() => {
    const addendums = this.addendumsResource.value() ?? [];

    return this.groupedLines().map(group => {
      const addendum = addendums.find(a => a.id === group.addendumId) ?? null;
      return {
        ...group,
        addendum,
        mismatchedProductIds: new Set(group.lines.map(line => line.addendumProductId))
      };
    });
  });

  isGroupExpanded(addendumId: string): boolean {
    return !this.collapsedAddendumIds().has(addendumId);
  }

  toggleGroupExpanded(addendumId: string): void {
    this.collapsedAddendumIds.update(ids => {
      const next = new Set(ids);
      if (next.has(addendumId)) {
        next.delete(addendumId);
      } else {
        next.add(addendumId);
      }
      return next;
    });
  }

  selectedAddendum = computed<DsmDealOption | null>(() => {
    const addendumId = this.selectedAddendumId();
    if (!addendumId) return null;
    return this.resolvedGroups().find(group => group.addendumId === addendumId)?.addendum ?? null;
  });

  showOptionDetails = computed(() => this.selectedAddendum() !== null && this.selectedProductId() !== null);

  onAddendumProductSelect(addendumId: string, productId: string, isKitLinkedProduct = false): void {
    this.selectedAddendumId.set(addendumId);
    this.selectedProductId.set(productId);
    this.selectedKitProductId = isKitLinkedProduct ? productId : null;
  }

  closeDetails(): void {
    this.selectedAddendumId.set(null);
    this.selectedProductId.set(null);
  }

  onOptionChanges(): void {
    this.reviewResource.reload();
    this.addendumsResource.reload();
    this.closeDetails();
  }

  executionLabel(execution: DsmRoomReplacementReviewExecution): string {
    return execution.triggerProductDescription || execution.triggerProductCode || 'A room replacement rule';
  }

  roomNames(rooms: DsmRoomReplacementReviewRoom[]): string {
    return (
      rooms
        .map(room => room.locationName)
        .filter(Boolean)
        .join(', ') || '—'
    );
  }
}
